import { resolveIndexInstrument } from '../../constants/indexInstruments';

export type CanonicalAssetClass = 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO' | 'UNKNOWN';

type KnownCanonicalAssetClass = Exclude<CanonicalAssetClass, 'UNKNOWN'>;

type TradeLike = Record<string, any>;

const FUTURES_ROOTS = new Set([
  'ES', 'MES', 'NQ', 'MNQ', 'RTY', 'M2K', 'YM', 'MYM', 'CL', 'GC', 'SI', 'HG', 'NG', 'ZB', 'ZN', 'ZF', 'ZT', '6A', '6B', '6C', '6E', '6J', '6N', '6S',
]);

const CRYPTO_ROOTS = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);

const FX_CODES = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK', 'HKD', 'SGD', 'CNH', 'CNY', 'MXN', 'ZAR', 'TRY', 'PLN', 'HUF', 'CZK', 'ILS', 'AED', 'SAR']);

const FUTURES_EXCHANGE_HINTS = ['CME', 'CBOT', 'NYMEX', 'COMEX', 'EUREX', 'CFE', 'ICE'];
const STOCK_EXCHANGE_HINTS = ['NASDAQ', 'NYSE', 'AMEX', 'ARCA'];
const CRYPTO_EXCHANGE_HINTS = ['BINANCE', 'COINBASE', 'KRAKEN', 'BYBIT', 'OKX', 'KUCOIN', 'GEMINI'];
const FOREX_EXCHANGE_HINTS = ['IDEALPRO', 'OANDA', 'FXCM', 'FOREX'];

function normalizeHint(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

function getFirstNonEmptyString(values: unknown[]): string | null {
  for (const value of values) {
    const next = String(value ?? '').trim();
    if (next) return next;
  }
  return null;
}

function getExecutionSample(trade: TradeLike): TradeLike | null {
  const collections = [trade?.executionsList, trade?.executions, trade?.mergedExecutions];
  for (const collection of collections) {
    if (!Array.isArray(collection) || collection.length === 0) continue;
    const sample = collection.find((entry) => entry && typeof entry === 'object');
    if (sample && typeof sample === 'object') return sample as TradeLike;
  }
  return null;
}

function sanitizeSymbol(raw: unknown): string {
  const value = String(raw ?? '').trim();
  if (!value) return '';

  const withoutExchange = value.includes(':') ? (value.split(':').pop() || value) : value;
  return withoutExchange.toUpperCase().trim().replace(/^\//, '').replace(/!$/g, '');
}

function normalizeContractSymbol(symbol: string): string {
  const compact = symbol.replace(/[\s._-]/g, '');
  const isFxPair = /^[A-Z]{3}[/.]?[A-Z]{3}$/.test(compact);
  if (isFxPair) return compact;

  const slashParts = compact.split('/').filter(Boolean);
  return slashParts.length > 0 ? slashParts[0] : compact;
}

function getCanonicalAssetClassFromExchange(value: unknown): KnownCanonicalAssetClass | null {
  const exchange = normalizeHint(value);
  if (!exchange) return null;
  if (FUTURES_EXCHANGE_HINTS.some((hint) => exchange.includes(hint))) return 'FUTURES';
  if (CRYPTO_EXCHANGE_HINTS.some((hint) => exchange.includes(hint))) return 'CRYPTO';
  if (FOREX_EXCHANGE_HINTS.some((hint) => exchange.includes(hint))) return 'FOREX';
  if (STOCK_EXCHANGE_HINTS.some((hint) => exchange.includes(hint))) return 'STOCKS';
  return null;
}

function getCanonicalAssetClassFromSymbol(symbol: unknown, exchange?: unknown): KnownCanonicalAssetClass | null {
  const sanitized = sanitizeSymbol(symbol);
  if (!sanitized) return getCanonicalAssetClassFromExchange(exchange);

  if (sanitized.includes(':')) {
    const fromExchange = getCanonicalAssetClassFromExchange(sanitized.split(':')[0]);
    if (fromExchange) return fromExchange;
  }

  const normalized = normalizeContractSymbol(sanitized);
  const contiguous = normalized.replace(/[/.]/g, '');

  if (CRYPTO_ROOTS.has(normalized) || CRYPTO_ROOTS.has(contiguous)) return 'CRYPTO';
  if (/^(BTC|ETH|LTC|XRP|DOGE|SOL|ADA|BNB)\b/.test(normalized) || /USDT$|USDC$|BUSD$|PERP$/.test(contiguous)) {
    return 'CRYPTO';
  }

  if (FUTURES_ROOTS.has(normalized)) return 'FUTURES';
  if (/^([A-Z0-9]{1,6})([FGHJKMNQUVXZ])(\d{1,2}|\d{4})$/.test(normalized)) return 'FUTURES';
  if (/^\d[A-Z]([FGHJKMNQUVXZ]\d{1,2})?$/.test(normalized)) return 'FUTURES';

  if (resolveIndexInstrument(sanitized)) return 'STOCKS';

  if (/^[A-Z]{3}[/.-][A-Z]{3}$/.test(normalized)) {
    const parts = normalized.split(/[/.-]/);
    const base = parts[0] ?? '';
    const quote = parts[1] ?? '';
    if (CRYPTO_ROOTS.has(base) || CRYPTO_ROOTS.has(quote)) return 'CRYPTO';
    if (FX_CODES.has(base) && FX_CODES.has(quote)) return 'FOREX';
  }

  if (/^[A-Z]{6}$/.test(contiguous)) {
    const base = contiguous.slice(0, 3);
    const quote = contiguous.slice(3, 6);
    if (CRYPTO_ROOTS.has(base) || CRYPTO_ROOTS.has(quote)) return 'CRYPTO';
    if (FX_CODES.has(base) && FX_CODES.has(quote)) return 'FOREX';
  }

  if (/^[A-Z]{1,5}$/.test(contiguous)) return 'STOCKS';

  return getCanonicalAssetClassFromExchange(exchange);
}

function toLegacyAssetClass(canonicalAssetClass: KnownCanonicalAssetClass): string {
  return canonicalAssetClass === 'FOREX' ? 'CASH' : canonicalAssetClass;
}

export function normalizeCanonicalAssetClass(value: unknown): CanonicalAssetClass | null {
  const hint = normalizeHint(value).replace(/[\s-]+/g, '_');
  if (!hint) return null;

  if (hint === 'UNKNOWN') return 'UNKNOWN';
  if (hint === 'INDEX_CFD' || hint === 'CASH_INDEX' || hint === 'INDEX' || hint === 'INDICES') return 'STOCKS';
  if (hint === 'STK' || hint.includes('STOCK') || hint.includes('EQUITY')) return 'STOCKS';
  if (hint === 'FUT' || hint.includes('FUTURE') || hint.includes('DERIVATIVE')) return 'FUTURES';
  if (hint === 'FX' || hint === 'CASH' || hint === 'CURRENCY_PAIR' || hint.includes('FOREX') || hint.includes('CURRENCY')) return 'FOREX';
  if (hint.includes('CRYPTO') || hint.includes('DIGITAL_ASSET') || hint.includes('DIGITAL') || hint.includes('COIN')) return 'CRYPTO';
  return null;
}

export function getCanonicalAssetClass(tradeLike: unknown): CanonicalAssetClass {
  if (!tradeLike || typeof tradeLike !== 'object') return 'UNKNOWN';

  const trade = tradeLike as TradeLike;
  const executionSample = getExecutionSample(trade);

  const explicitCandidates = [
    trade?.canonicalAssetClass,
    trade?.assetClass,
    trade?.marketType,
    trade?.instrumentType,
    trade?.secType,
    trade?.asset_type,
    trade?.assetType,
    trade?.market,
    trade?.category,
    trade?.securityType,
    trade?.type,
    trade?.instrument?.assetClass,
    trade?.instrument?.instrumentType,
    trade?.instrument?.secType,
    executionSample?.canonicalAssetClass,
    executionSample?.assetClass,
    executionSample?.marketType,
    executionSample?.instrumentType,
    executionSample?.secType,
    executionSample?.asset_type,
    executionSample?.assetType,
  ];

  for (const candidate of explicitCandidates) {
    const normalized = normalizeCanonicalAssetClass(candidate);
    if (normalized) return normalized;
  }

  const symbol = getFirstNonEmptyString([
    trade?.symbol,
    trade?.localSymbol,
    trade?.ticker,
    trade?.instrumentSymbol,
    trade?.instrument?.symbol,
    executionSample?.symbol,
  ]);

  const exchange = getFirstNonEmptyString([
    trade?.exchange,
    trade?.primaryExchange,
    trade?.instrument?.exchange,
    executionSample?.exchange,
  ]);

  const symbolBased = getCanonicalAssetClassFromSymbol(symbol, exchange);
  if (symbolBased) return symbolBased;

  const sourceHint = getFirstNonEmptyString([
    trade?.broker,
    trade?.source,
    trade?.importSource,
    trade?.brokerId,
  ]);
  const sourceBased = getCanonicalAssetClassFromExchange(sourceHint);
  if (sourceBased) return sourceBased;

  return 'UNKNOWN';
}

export function normalizeTradeAssetClassification<T extends TradeLike>(trade: T): T & { canonicalAssetClass: CanonicalAssetClass; assetClass?: string } {
  const canonicalAssetClass = getCanonicalAssetClass(trade);

  const next: Record<string, any> = {
    ...trade,
    canonicalAssetClass,
  };

  if (canonicalAssetClass !== 'UNKNOWN' && !String(next.assetClass ?? '').trim()) {
    next.assetClass = toLegacyAssetClass(canonicalAssetClass);
  }

  if (canonicalAssetClass === 'STOCKS') {
    const indexMatch = resolveIndexInstrument(
      next.symbol ?? next.localSymbol ?? next.ticker,
      next.instrumentType ?? next.instrument,
      next
    );
    if (indexMatch) {
      if (!next.instrumentType) next.instrumentType = indexMatch.instrumentType;
      if (!next.canonicalSymbol) next.canonicalSymbol = indexMatch.canonicalSymbol;
      if (!next.indexName) next.indexName = indexMatch.indexName;
    }
  }

  return next as T & { canonicalAssetClass: CanonicalAssetClass; assetClass?: string };
}